import pako from 'pako'; // npm install pako
import {
  ChunkedMessage,
  WEBSOCKET_CONFIG,
  generateChunkId,
  needsChunking,
  calculateChunks,
} from '../utils/chunking';
import { useDebugger } from '../contexts/DebuggerContext';

export interface ChunkingOptions {
  onProgress?: (sent: number, total: number) => void;
}

/**
 * Compress data using gzip
 */
async function compressData(data: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const uint8Array = encoder.encode(data);

  // Only compress if over threshold
  if (uint8Array.length < WEBSOCKET_CONFIG.COMPRESSION_THRESHOLD) {
    return uint8Array;
  }

  return pako.gzip(uint8Array);
}

/**
 * Convert Uint8Array to base64 string
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Split compressed data into chunks and create chunk messages
 */
function createChunks(
  compressedData: Uint8Array,
  requestId: string,
  originalSize: number,
  compressed: boolean,
): ChunkedMessage[] {
  const chunks: ChunkedMessage[] = [];
  const chunkId = generateChunkId();
  const totalChunks = calculateChunks(compressedData.length);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * WEBSOCKET_CONFIG.CHUNK_SIZE;
    const end = Math.min(
      start + WEBSOCKET_CONFIG.CHUNK_SIZE,
      compressedData.length,
    );
    const chunkData = compressedData.slice(start, end);

    const chunk: ChunkedMessage = {
      type: 'chunk',
      chunkId,
      requestId,
      sequence: i,
      totalChunks,
      data: uint8ArrayToBase64(chunkData),
    };

    // Add metadata to first chunk
    if (i === 0) {
      chunk.originalSize = originalSize;
      chunk.compressed = compressed;
    }

    chunks.push(chunk);
  }

  return chunks;
}

/**
 * Hook for chunking large WebSocket messages
 */
export function useWebSocketChunking(
  sendRawMessage: (message: any) => Promise<any>,
) {
  const debug = useDebugger();

  /**
   * Send a potentially large message with automatic chunking
   */
  const sendChunkedMessage = async (
    request: any,
    options?: ChunkingOptions,
  ): Promise<void> => {
    const messageStr = JSON.stringify(request);
    const originalSize = new Blob([messageStr]).size;

    // If message is small enough, send directly
    if (!needsChunking(messageStr)) {
      await sendRawMessage(request);
      return;
    }

    const compressed = originalSize > WEBSOCKET_CONFIG.COMPRESSION_THRESHOLD;
    const compressedData = await compressData(messageStr);

    // Create chunks
    const chunks = createChunks(
      compressedData,
      request.requestId,
      originalSize,
      compressed,
    );

    // Send all chunks
    for (let i = 0; i < chunks.length; i++) {
      await sendRawMessage({
        action: 'CHUNK',
        requestId: chunks[i].requestId,
        payload: chunks[i],
      });

      options?.onProgress?.(i + 1, chunks.length);
    }
  };

  return { sendChunkedMessage };
}
